<template>
  <div class="mini-calendar">
    <div class="mini-calendar-header">
      <div class="mini-calendar-month">{{ currentMonth }}</div>
      <div class="mini-calendar-nav">
        <img
          class="mini-calendar-nav-button mini-calendar-nav-button-left"
          @click="previousMonth"
          src="/resources/images/new-ui/routing/right-arrow.svg"
          alt="arrow-left"
        />
        <img
          class="mini-calendar-nav-button mini-calendar-nav-button-right"
          @click="nextMonth"
          src="/resources/images/new-ui/routing/right-arrow.svg"
          alt="arrow-right"
        />
      </div>
    </div>

    <div class="mini-calendar-weekdays">
      <div
        v-for="(weekday, index) in weekdays"
        :key="index"
        class="mini-calendar-weekday"
        :class="{ active: isActiveWeekday(index) }"
      >
        {{ weekday }}
      </div>
    </div>

    <div class="mini-calendar-grid">
      <div
        v-for="(day, index) in calendarDays"
        :key="index"
        class="mini-calendar-day"
        :class="{
          'other-month': day.otherMonth,
          today: day.isToday,
          selected: day.isSelected,
        }"
        @click="selectDate(day)"
      >
        {{ day.day }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MiniCalendar',
  props: {
    initialDate: {
      type: Date,
      default: () => new Date(),
    },
    selectedDate: {
      type: Date,
      default: () => new Date(),
    },
  },
  data() {
    return {
      currentDate: new Date(this.initialDate),
      weekdays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    }
  },
  computed: {
    currentMonth() {
      return this.currentDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      })
    },
    calendarDays() {
      return this.generateCalendarDays()
    },
    today() {
      return new Date()
    },
  },
  watch: {
    initialDate: {
      handler(newDate) {
        this.currentDate = new Date(newDate)
      },
      immediate: true,
    },
  },
  methods: {
    generateCalendarDays() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      const firstDayOfMonth = new Date(year, month, 1)
      const lastDayOfMonth = new Date(year, month + 1, 0)
      const firstDayOfWeek = firstDayOfMonth.getDay()
      const daysInMonth = lastDayOfMonth.getDate()

      const days = []
      const today = new Date()

      // Previous month days
      if (firstDayOfWeek > 0) {
        const prevMonthYear = month === 0 ? year - 1 : year
        const prevMonth = month === 0 ? 11 : month - 1
        const prevMonthLastDay = new Date(
          prevMonthYear,
          prevMonth + 1,
          0,
        ).getDate()

        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
          const day = prevMonthLastDay - i
          days.push({
            day,
            date: new Date(prevMonthYear, prevMonth, day),
            otherMonth: true,
            isToday: false,
            isSelected: false,
          })
        }
      }

      // Current month days
      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(year, month, day)
        const isToday = this.isSameDay(dayDate, today)
        const isSelected = this.isSameDay(dayDate, this.selectedDate)

        days.push({
          day,
          date: dayDate,
          otherMonth: false,
          isToday,
          isSelected,
        })
      }

      // Next month days
      const totalCells = 42 // 6 rows × 7 days
      const remainingCells = totalCells - days.length
      const nextMonthYear = month === 11 ? year + 1 : year
      const nextMonth = month === 11 ? 0 : month + 1

      for (let day = 1; day <= remainingCells; day++) {
        days.push({
          day,
          date: new Date(nextMonthYear, nextMonth, day),
          otherMonth: true,
          isToday: false,
          isSelected: false,
        })
      }

      return days
    },
    isSameDay(date1, date2) {
      return (
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      )
    },
    previousMonth() {
      const newMonth = this.currentDate.getMonth() - 1
      const newYear =
        newMonth < 0
          ? this.currentDate.getFullYear() - 1
          : this.currentDate.getFullYear()
      this.currentDate = new Date(newYear, newMonth < 0 ? 11 : newMonth, 1)
      this.$emit('month-changed', this.currentDate)
    },
    nextMonth() {
      const newMonth = this.currentDate.getMonth() + 1
      const newYear =
        newMonth > 11
          ? this.currentDate.getFullYear() + 1
          : this.currentDate.getFullYear()
      this.currentDate = new Date(newYear, newMonth > 11 ? 0 : newMonth, 1)
      this.$emit('month-changed', this.currentDate)
    },
    selectDate(day) {
      this.$emit('date-selected', day.date)
    },
    isActiveWeekday(weekdayIndex) {
      // Get today's day of the week (0 = Sunday, 1 = Monday, etc.)
      const todayWeekday = this.today.getDay()
      return weekdayIndex === todayWeekday
    },
  },
}
</script>

<style lang="scss" scoped>
.mini-calendar {
  margin-bottom: 12px;
  border: 1px solid #c1d6e1;
  border-radius: 8px;
  padding: 12px 13px;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  &-month {
    color: #152f4a;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
  }

  &-nav {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: center;

    &-button {
      height: 12px;
      cursor: pointer;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.7;
      }

      &-left {
        transform: rotate(180deg);
      }
    }
  }

  &-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    margin-bottom: 5px;
  }

  &-weekday {
    text-align: center;
    color: #c1d6e1;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 600;
    line-height: 15px;
    padding: 4px 6px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &.active {
      color: #152f4a;
    }
  }

  &-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 5px;
    place-items: center;
  }

  &-day {
    text-align: center;
    padding: 1px;
    color: #152f4a;
    text-align: center;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    width: 22px;
    height: 22px;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
      background-color: #e9ecef;
    }

    &.today {
      background-color: #aee0bf;
      border: 1px solid #152f4a;
      border-radius: 4px;
    }

    &.selected {
      background-color: #aee0bf;
      color: #007bff;
    }

    &.other-month {
      color: #c1d6e1;
    }
  }
}
</style>
